import React, { useEffect, useState } from "react";
import { App, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useBlocker, useLocation } from "react-router";
import { HomeOutlined } from "@ant-design/icons";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import CampaignList from "./List";
import Builder from "./Builder";
import { AUDIENCES, CampaignRow, EmailTemplate } from "./data";
import TemplateGrid from "./TemplateGrid";
import CampaignPreview from "./Preview";

/**
 * The section's screens are switched by local state rather than routes.
 *
 * Each screen is a component that knows nothing about navigation and takes
 * callbacks, so turning these into real routes later means replacing this file
 * alone — the screens themselves do not change. Until then there are no deep
 * links: /campaigns is one address for every step, and the browser's back
 * button leaves the section. See DEVELOPER_TASKS.md.
 */
export type CampaignStep = "list" | "build" | "send";

/** What opened the builder, and what the trail calls it. */
export type CampaignAction = "create" | "preview" | "edit" | "copy";

/**
 * What each way into the builder is called in the breadcrumb.
 *
 * Copy shares Create's. Starting from an existing campaign does copy its
 * letter, but that is how it is built, not what the user asked for: they
 * pressed a button called Create campaign, and a trail that then says
 * "Duplicate" is the app talking about its own implementation.
 */
const ACTION_LABEL: Record<CampaignAction, string> = {
  create: "common.create",
  preview: "pages.campaigns.actions.preview",
  edit: "pages.campaigns.actions.edit",
  copy: "common.create",
};

export default function CampaignsContent() {
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [step, setStep] = useState<CampaignStep>("list");
  // Set by the builder while the letter holds work that is not saved as a
  // template. Leaving with it set is worth a question.
  const [dirty, setDirty] = useState(false);
  const { modal, message } = App.useApp();
  // A campaign starts from a template, so Create asks which one before the
  // builder opens. The chosen one is what the builder lays out.
  const [picking, setPicking] = useState(false);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  // The row being previewed. A preview is something you read beside the list,
  // so it opens a drawer rather than the builder.
  const [previewRow, setPreviewRow] = useState<CampaignRow | null>(null);
  // Which action opened the builder, so the trail can name it. The row actions
  // already carry the mode; it used to be discarded here.
  const [mode, setMode] = useState<CampaignAction>("create");
  /** What the campaign being worked on is called, where it is called anything
   * already. Empty for a new one, which is asked for when it is saved. */
  const [subject, setSubject] = useState("");
  /** Who it goes to, carried over from the campaign it was opened from. */
  const [audience, setAudience] = useState<string | undefined>();
  /** The campaign the builder was opened on, where it was opened on one. */
  const [editing, setEditing] = useState<CampaignRow | null>(null);
  /**
   * Campaigns deleted in this session, by key.
   *
   * Here rather than in the table because three screens delete: the row's own
   * action, the preview drawer and the builder's header. One list and one
   * dialog, so the three cannot come to mean different things.
   *
   * TODO(design): needs an endpoint. Nothing leaves the browser, so a deleted
   * campaign is back on the next visit. See DEVELOPER_TASKS.md.
   */
  const [deleted, setDeleted] = useState<string[]>([]);
  const location = useLocation();

  /**
   * Runs `next` once the user has agreed to lose the letter, or straight away
   * when there is nothing to lose. Uses the same confirm the builder asks
   * before a template replaces a letter — one dialog for one kind of question.
   */
  const leaveFlow = (next: () => void) => {
    if (!dirty) {
      next();
      return;
    }

    modal.confirm({
      title: t("pages.campaigns.discardTitle"),
      content: t("pages.campaigns.discardText"),
      okText: t("pages.campaigns.discard"),
      okButtonProps: { danger: true },
      onOk: () => {
        setDirty(false);
        next();
      },
    });
  };

  /**
   * Deletes a campaign, once. Named in the dialog, since a list of campaigns is
   * read by subject and the rows either side look much the same, and `then` is
   * where the screen that asked puts itself away — the drawer closes, the
   * builder returns to the list.
   */
  const deleteCampaign = (row: CampaignRow, then?: () => void) =>
    modal.confirm({
      title: t("pages.campaigns.deleteTitle"),
      content: (
        <span>
          {t("pages.campaigns.deleteText")} <b>{row.subject}</b>?
        </span>
      ),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => {
        setDeleted((keys) => [...keys, row.key]);
        message.success(t("pages.campaigns.deleted", { name: row.subject }));
        then?.();
      },
    });

  // Static trail, no derivation: the shell resets the breadcrumbs on every
  // screen and only the screen itself fills them in. Building them from the
  // route is a developer's job — see DEVELOPER_TASKS.md. Here the depth comes
  // from the step instead, since these screens are state rather than routes:
  // the list is one level in, the builder two, and the second level goes back
  // to the list the way a section link would.
  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      step === "list"
        ? { title: t("routes.campaigns") }
        : {
            // A link in markup, not in the item: BreadcrumbsItem carries a title
            // and an href and nothing else, and this one has to ask before it
            // goes anywhere. The href keeps it a real link — right-click, open
            // in a new tab, and the address the browser shows.
            title: (
              <a
                href="/campaigns"
                onClick={(event) => {
                  event.preventDefault();
                  leaveFlow(() => setStep("list"));
                }}
              >
                {t("routes.campaigns")}
              </a>
            ),
          },
      ...(step === "list"
        ? []
        : [
            // Past the builder the crumb becomes the way back to it — the same
            // job a section link does one level up.
            step === "build"
              ? { title: t(ACTION_LABEL[mode]) }
              : {
                  // Past the builder this crumb is the way back to it — the
                  // same job the section link does one level up, and no
                  // question to ask, since the letter is not being left.
                  title: (
                    <a
                      href="/campaigns"
                      onClick={(event) => {
                        event.preventDefault();
                        setStep("build");
                      }}
                    >
                      {t(ACTION_LABEL[mode])}
                    </a>
                  ),
                },
          ]),
      ...(step === "list" || step === "build"
        ? []
        : [{ title: t("pages.campaigns.send.title") }]),
    ]);
  }, [i18n.language, t, step, mode, dirty]);

  /**
   * Leaving the section altogether — a menu item, the browser's back button,
   * anything that changes the address.
   *
   * The breadcrumb and a second click on Campaigns are handled below, because
   * they do not change the address and so the router never hears about them.
   * Everything that does needs the router's own blocker: the section is
   * unmounted by a navigation, and a component on its way out has no chance to
   * ask anything.
   *
   * Same-address navigations are let through — that is the second click on
   * Campaigns, and the effect below already asks about it. Blocking here as
   * well would ask twice.
   */
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    modal.confirm({
      title: t("pages.campaigns.discardTitle"),
      content: t("pages.campaigns.discardText"),
      okText: t("pages.campaigns.discard"),
      okButtonProps: { danger: true },
      onOk: () => {
        setDirty(false);
        blocker.proceed();
      },
      // Both ways out of the dialog put the navigation back, or the blocker
      // stays armed and the next click does nothing at all.
      onCancel: () => blocker.reset(),
    });
  }, [blocker.state]);

  /**
   * Closing the tab or reloading it. The browser will not show our dialog here
   * — it shows its own, unstyled and unworded, and only because the page said
   * it had something to lose. That is the whole of what a page is allowed.
   */
  useEffect(() => {
    if (!dirty) return undefined;

    const ask = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener("beforeunload", ask);

    return () => window.removeEventListener("beforeunload", ask);
  }, [dirty]);

  // Clicking Campaigns in the menu opens Campaigns, even when the section is
  // already the one on screen. Each click pushes a new history entry with its
  // own key, so watching the key returns the section to its list. This is a
  // stopgap: once the steps are routes of their own, the router does it and
  // this effect goes — see DEVELOPER_TASKS.md.
  useEffect(() => {
    // The address is /campaigns either way, so staying put after a cancel
    // leaves nothing out of step.
    leaveFlow(() => setStep("list"));
  }, [location.key]);

  /**
   * Opens the builder on a campaign, carrying across what it already knows.
   *
   * Editing keeps the campaign's own subject: it has one, and saving it again
   * is not the moment to ask for the name it was found under. Copying takes
   * everything the campaign had and marks the name, so the two are told apart
   * in a list that shows them side by side. Creating starts empty and is asked
   * for a name when it is first saved.
   */
  const openBuilder = (action: CampaignAction, row?: CampaignRow) => {
    setSubject(
      row && action === "edit"
        ? row.subject
        : row && action === "copy"
          ? t("pages.campaigns.copyOf", { name: row.subject })
          : "",
    );
    // The sending details come with it. From name and reply-to are the
    // account's own and are the same on every campaign, so the audience is the
    // one that has to travel.
    setAudience(
      row && action !== "create"
        ? AUDIENCES.find((item) => item.name === row.audience)?.id
        : undefined,
    );
    setEditing(row && action === "edit" ? row : null);
    setMode(action);
    setStep("build");
  };

  /** Picked from the dialog or from the Templates view — the same start. */
  const startFrom = (picked: EmailTemplate) => {
    setPicking(false);
    setTemplate(picked);
    openBuilder("create");
  };

  // One component for the three screens of the flow: the letter lives in the
  // builder, so the send step and its confirmation are shown from inside it
  // rather than replacing it.
  if (step !== "list") {
    return (
      <Builder
        step={step}
        mode={mode}
        onStep={setStep}
        onExit={() => setStep("list")}
        template={template}
        subject={subject}
        audience={audience}
        // Only a campaign that exists can be deleted: a new one has nothing
        // behind it yet, and a copy is a new one.
        onDelete={
          editing
            ? () =>
                deleteCampaign(editing, () => {
                  setDirty(false);
                  setStep("list");
                })
            : undefined
        }
        onDirty={setDirty}
      />
    );
  }

  return (
    <>
      <CampaignList
        deleted={deleted}
        onCreate={() => setPicking(true)}
        onOpen={(row: CampaignRow, action) => {
          if (action === "preview") {
            setPreviewRow(row);
            return;
          }

          // An existing campaign brings its own letter; the template picked
          // last is not it.
          setTemplate(null);
          openBuilder(action, row);
        }}
      />

      <CampaignPreview
        row={previewRow}
        onClose={() => setPreviewRow(null)}
        onDuplicate={() => {
          setPreviewRow(null);
          setTemplate(null);
          openBuilder("copy");
        }}
        onEdit={() => {
          setPreviewRow(null);
          setTemplate(null);
          openBuilder("edit", previewRow ?? undefined);
        }}
        onDelete={(row) => deleteCampaign(row, () => setPreviewRow(null))}
      />

      {/* Which letter the campaign starts as. The same cards the Templates
          view shows, in a dialog — one way to choose a template, shown in two
          places. */}
      <Modal
        open={picking}
        title={t("pages.campaigns.pickTemplate")}
        footer={null}
        width={720}
        onCancel={() => setPicking(false)}
        destroyOnHidden
      >
        <TemplateGrid onPick={startFrom} />
      </Modal>
    </>
  );
}
